import React, { FC, useMemo } from 'react'
import classNames from 'classnames'
import { DefaultAvatar } from '@/components/icons'
import useTranslation from 'next-translate/useTranslation'
import NextImage from 'next/image'
import formatLocation from '@/utils/formatLocation'
import { MapPinCheckInside, Wallet, Car } from 'lucide-react'
import { TalentPublicReadManyDto } from '@/client-sdk'

const CountryCodeMap: Record<string, string> = {
	polish: 'pl',
	english: 'en',
	german: 'de',
	french: 'fr',
	spanish: 'es',
	italian: 'it',
	dutch: 'nl',
	norwegian: 'no',
	swedish: 'se',
	danish: 'dk',
	other: 'xx',
}

export interface ITalentCard extends React.PropsWithChildren {
	candidateLabel: string
	talent: TalentPublicReadManyDto
}

export const TalentCard: FC<ITalentCard> = ({ candidateLabel, talent }) => {
	const { t } = useTranslation('talent-pool')
	const { t: tTalent } = useTranslation('talent')
	const { t: tCommon } = useTranslation('common')
	const {
		positionName: jobTitle,
		user: { avatar = '' },
		availableSince,
		country,
		salaryRange,
		currencySalary,
		drivingLicense = [],
		languages = [],
		nativeLanguage,
		isLookingForJob: lookingJob,
		experienceYears,
	} = talent

	const getAvailableSinceStyles = (availableSince: any) => {
		switch (availableSince?.toString()) {
			case 'asap': {
				return 'bg-[#DDF0DD] text-[#006400]'
			}
			case '2_weeks': {
				return 'bg-[#D1EFFC] text-[#01579B]'
			}
			case '1_month': {
				return 'bg-[#D6D1FC] text-[#311B92]'
			}
			case '2_months': {
				return 'bg-[#FFE4C4] text-[#D84315]'
			}
			case '3_months': {
				return 'bg-[#FDE2E2] text-[#D32F2F]'
			}
			default: {
				return 'bg-gray-200 text-gray-900'
			}
		}
	}

	const formatExperienceYears = () => {
		if (!experienceYears) return ''

		const expYears = experienceYears.toString().replace('_', '.')
		let numYears = Number.parseFloat(expYears)

		if (expYears === '10+' || expYears === '10') {
			numYears = 10
		}

		let translationKey
		if (numYears === 1) {
			translationKey = 'experience-year.1'
		} else if (numYears >= 2 && numYears <= 4) {
			translationKey = 'experience-year.2_4'
		} else {
			translationKey = 'experience-year.5+'
		}

		return tTalent(translationKey, { years: numYears.toString() })
	}

	const allLanguages = useMemo(() => {
		const result = []

		if (nativeLanguage) {
			result.push({
				language: nativeLanguage,
			})
		}

		if (languages && languages.length > 0) {
			for (const lang of languages) {
				if (String(lang.language) !== String(nativeLanguage)) {
					result.push(lang)
				}
			}
		}

		return result
	}, [languages, nativeLanguage])

	return (
		<div
			className={classNames(
				'cursor-pointer font-lexend bg-white border border-solid border-brand-gray-300 group shadow-md rounded-xl group h-full overflow-hidden relative duration-300 group hover:shadow-2xl hover:border-brand-primary-md flex flex-col',
			)}
		>
			<div className="group absolute top-0 left-0 w-full h-[6px] bg-brand-primary-md opacity-0 group group-hover:opacity-100 transition-opacity duration-300"></div>
			<div className="p-4 md:p-6">
				<div className="flex flex-col gap-6 font-medium">
					<div className="flex justify-between">
						<div className="flex gap-3 items-center w-2/3">
							<div>
								{avatar ? (
									<NextImage
										src={avatar}
										alt="logo"
										width={60}
										height={60}
										className="aspect-square rounded-full max-w-[50px] max-h-[50px] object-center object-contain"
										onError={e => {
											e.currentTarget.style.opacity = '0'
										}}
									/>
								) : (
									<DefaultAvatar className="w-12 h-12" />
								)}
							</div>
							<div className="flex flex-col">
								<p className="text-lg font-semibold text-brand-gray-400 line-clamp-1">
									{tTalent(`positions.${jobTitle}`)}
								</p>
								<p className="text-xs text-brand-gray-400 font-medium">
									{experienceYears ? formatExperienceYears() : candidateLabel}
								</p>
							</div>
						</div>
						<div>
							{lookingJob ? (
								<div className="bg-green-100 rounded-xl px-3 py-1 flex-shrink-0">
									<span className="text-xs font-semibold text-green-800">{tTalent('looking-for-job')}</span>
								</div>
							) : (
								''
							)}
						</div>
					</div>

					<div className="grid grid-cols-2 gap-y-3 w-full text-opacity-70 text-brand-gray-400">
						<span className="flex items-center gap-2 py-1 md:px-1 rounded-full">
							<Wallet className="w-5 h-5 text-brand-primary-md shrink-0" strokeWidth={1.5} />
							<span className="text-xs md:text-sm font-medium text-brand-gray-400 line-clamp-1">
								{salaryRange?.toString() === 'negotiable'
									? tTalent('salary.negotiable')
									: `${salaryRange} ${currencySalary}`}
							</span>
						</span>

						<span className="flex items-center gap-2 py-1 md:px-2 rounded-full">
							<MapPinCheckInside className="w-5 h-5 text-brand-primary-md shrink-0" strokeWidth={1.5} />
							<span className="text-xs md:text-sm font-medium text-brand-gray-400 line-clamp-1">
								{formatLocation(tCommon(`countries.${country}`))}
							</span>
						</span>

						<span className="flex items-center gap-2 py-1 md:px-1 rounded-full relative group/tooltip">
							<Car className="w-5 h-5 text-brand-primary-md shrink-0" strokeWidth={1.5} />
							<span className="text-xs md:text-sm font-medium text-brand-gray-400 line-clamp-1 cursor-help">
								{drivingLicense && drivingLicense.length > 0 ? drivingLicense.join(', ') : t('no-driving-license')}
							</span>
							<div className="absolute invisible group-hover/tooltip:visible opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-300 bottom-[calc(100%+10px)] left-0 p-2 bg-gray-900 text-white text-xs rounded w-80 shadow-lg z-[9999] pointer-events-none">
								{tTalent('driving-license-tooltip')}
								<div className="absolute bottom-[-8px] left-4 border-x-8 border-x-transparent border-t-8 border-t-gray-900"></div>
							</div>
						</span>

						{availableSince && (
							<span className="flex items-center gap-2 py-1 md:px-2 rounded-full relative group/tooltip">
								<div
									className={`px-3 py-1 rounded-2xl text-xs md:text-sm cursor-help ${getAvailableSinceStyles(availableSince?.toString())}`}
								>
									{t(`available-since.${availableSince}`)}
								</div>
								<div className="absolute invisible group-hover/tooltip:visible opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-300 bottom-full left-0 mb-2 p-2 bg-gray-900 text-white text-xs rounded w-80 shadow-lg z-[9999] max-w-40">
									{t('available-since-tooltip')}
									<div className="absolute bottom-0 left-4 -mb-1 border-x-8 border-x-transparent border-t-8 border-t-gray-900"></div>
								</div>
							</span>
						)}
					</div>
				</div>
			</div>

			<div className="mt-auto w-full rounded-b-xl overflow-hidden flex-grow">
				<div className="bg-gray-100 p-4 md:px-5 space-y-5 h-full">
					<div className="flex items-center">
						<p className="uppercase text-[10px] text-brand-gray-300 font-bold tracking-widest mr-3">{t(`i-speak`)}:</p>
						{allLanguages.length > 0 ? (
							<div className="flex flex-wrap items-center gap-2">
								{allLanguages.map((lang, index) => {
									const flagCode = CountryCodeMap[String(lang.language)] || 'xx'

									return (
										<div key={index} className="w-6 h-6 relative group/lang">
											<NextImage
												src={`/flags/${flagCode}.png`}
												alt={tCommon(`languages.${lang.language}`)}
												width={24}
												height={16}
												className="rounded-sm object-cover"
											/>
										</div>
									)
								})}
							</div>
						) : (
							<div className="h-6 flex items-center">
								<span className="text-xs text-brand-gray-300 font-semibold">{t('no-languages')}</span>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

TalentCard.displayName = 'TalentCard'
